import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { useUsers } from '../../hooks/useUsers';
import { useDeleteGroup, useUpdateGroup } from '../../hooks/useTeamChat';
import type { TeamConversation } from '../../lib/teamChatApi';
import { useConfirm } from '../ui/ConfirmProvider';
import { MemberPicker } from './MemberPicker';

/** Admin-only: rename a group, change its members, or delete it. */
export function GroupSettingsModal({
  conversation,
  open,
  onClose,
}: {
  conversation: TeamConversation;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { data: users } = useUsers();
  const updateGroup = useUpdateGroup();
  const deleteGroup = useDeleteGroup();
  const [name, setName] = useState(conversation.name ?? '');
  const [memberIds, setMemberIds] = useState<string[]>(conversation.members.map((m) => m.id));
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const options = (users ?? [])
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((u) => ({ id: u.id, name: u.name }));

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const current = new Set(conversation.members.map((m) => m.id));
    const next = new Set(memberIds);
    const addMemberIds = memberIds.filter((id) => !current.has(id));
    const removeMemberIds = [...current].filter((id) => !next.has(id));
    setError(null);
    updateGroup.mutate(
      {
        id: conversation.id,
        patch: {
          ...(trimmed !== conversation.name ? { name: trimmed } : {}),
          ...(addMemberIds.length > 0 ? { addMemberIds } : {}),
          ...(removeMemberIds.length > 0 ? { removeMemberIds } : {}),
        },
      },
      {
        onSuccess: () => onClose(),
        onError: (err) => setError(err instanceof Error ? err.message : String(err)),
      },
    );
  };

  const remove = async () => {
    if (
      !(await confirm({
        message: t('teamChat.confirmDeleteGroup', { name: conversation.name ?? '' }),
        danger: true,
      }))
    ) {
      return;
    }
    deleteGroup.mutate(conversation.id, {
      onSuccess: () => {
        onClose();
        navigate('/team-chat');
      },
      onError: (err) => setError(err instanceof Error ? err.message : String(err)),
    });
  };

  return (
    <div
      className="ui-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={t('teamChat.groupSettings')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        className="ui-modal-panel flex w-full max-w-md flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <h2 className="font-heading text-base font-semibold text-foreground">
          {t('teamChat.groupSettings')}
        </h2>
        <label className="ui-field-label mt-3 block" htmlFor="team-chat-group-rename">
          {t('teamChat.groupName')}
        </label>
        <input
          id="team-chat-group-rename"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          className="ui-input"
        />
        <span className="ui-field-label mt-3 block">{t('teamChat.members')}</span>
        {/* On a phone the panel is full screen: the list takes the free
            height, so the buttons sit at the bottom edge instead of
            leaving half a screen of nothing under them. */}
        <MemberPicker
          options={options}
          value={memberIds}
          onChange={setMemberIds}
          className="max-sm:min-h-0 max-sm:max-h-none max-sm:flex-1"
        />
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="flex items-center justify-between gap-2 pt-5 max-sm:pb-[env(safe-area-inset-bottom)]">
          <button
            type="button"
            onClick={() => void remove()}
            disabled={deleteGroup.isPending}
            className="ui-btn-danger disabled:opacity-50"
          >
            {t('teamChat.deleteGroup')}
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="ui-btn-secondary">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!name.trim() || updateGroup.isPending}
              className="ui-btn-primary disabled:opacity-50"
            >
              {t('common.save')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
